import React, { useState } from 'react';
import { OptionIVFailureReason, OptionIVResult } from 'utils/shared/quantlib/BlackScholes';

const failureMessages: Record<OptionIVFailureReason, string> = {
  'below-intrinsic': 'Premium is below the model price floor.',
  'above-ceiling': 'Premium exceeds the model price ceiling.',
  'not-bracketed': 'No IV was found within the model search range (0.1%–500%).',
  'no-convergence': 'The IV solver did not converge.',
  'missing-vega-fn': 'The IV solver is missing a required sensitivity.',
  'invalid-input': 'The option inputs are invalid or incomplete.',
  expired: 'IV is not identifiable at the app’s expiry settlement cutoff.',
  'missing-premium': 'No observed entry premium is available.',
};

interface Props {
  results: OptionIVResult[];
  volatility: number | null;
  isManual: boolean;
  onEstimate: (volatility: number) => void;
  onReset: () => void;
}

export function parseEstimatedVolatility(percent: string): number | null {
  const value = Number(percent) / 100;
  return percent.trim() && Number.isFinite(value) && value >= 0.001 && value <= 5 ? value : null;
}

export default function VolatilityControl({
  results,
  volatility,
  isManual,
  onEstimate,
  onReset,
}: Props) {
  const [estimate, setEstimate] = useState('');
  const parsedEstimate = parseEstimatedVolatility(estimate);
  const failed = results.filter((result) => result.status === 'unavailable');

  return (
    <section
      className="mx-2 mt-3 rounded-md bg-palette-black-4 p-3 text-xs"
      aria-label="Volatility assumptions"
    >
      <p role="status">
        {isManual ? 'Manual / estimated volatility' : 'Implied Volatility'}:{' '}
        {volatility == null ? 'Unavailable' : `${(volatility * 100).toFixed(1)}%`}
        {isManual && ' — used for valuation; not a successful IV solve.'}
      </p>
      {failed.length > 0 && (
        <div className="mt-2 text-amber-200">
          <p>
            {failed.length} of {results.length} leg IVs unavailable. No partial IV average is used.
            Review the quote/model assumptions or explicitly enter an estimate.
          </p>
          <ul className="mt-1 list-inside list-disc">
            {results.map(
              (result, index) =>
                result.status === 'unavailable' && (
                  <li key={index}>
                    Leg {index + 1}: {failureMessages[result.reason]} ({result.reason})
                  </li>
                ),
            )}
          </ul>
        </div>
      )}
      <form
        className="mt-2 flex flex-wrap items-center gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (parsedEstimate != null) onEstimate(parsedEstimate);
        }}
      >
        <label htmlFor="manual-volatility">Manual estimate (%)</label>
        <input
          id="manual-volatility"
          type="number"
          min="0.1"
          max="500"
          step="any"
          value={estimate}
          onChange={(event) => setEstimate(event.target.value)}
          className="w-24 rounded bg-palette-black-2 p-2"
          aria-describedby="manual-volatility-help"
        />
        <button
          type="submit"
          disabled={parsedEstimate == null}
          className="rounded bg-brand-primary px-3 py-2 disabled:opacity-50"
        >
          Use estimate
        </button>
        {isManual && (
          <button type="button" onClick={onReset} className="underline">
            Use quote IV
          </button>
        )}
        <span id="manual-volatility-help">0.1%–500%. Applies to every option leg.</span>
      </form>
    </section>
  );
}
